import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { attachAuthHeader } from './interceptors/request/auth-interceptor';
import { attachSpaceIdHeader } from './interceptors/request/space-id-interceptor';
import { getDeviceId } from '@/shared/utils/device-id';
import { getRegion } from '@/shared/utils/region';
import { getNetworkInfo } from '@/shared/utils/network-info';
import {
  getFlowTraceContext,
  formatTraceLog,
} from '@/shared/lib/trace-context';
import {
  handleResponse,
  handleResponseError,
} from './interceptors/response/response-interceptor';

/**
 * 코인 부족 에러 코드
 */
export const NOT_ENOUGH_COINS_ERROR_CODE = 1301;

/**
 * URL 쿼리 파라미터 타입 정의
 */
type QueryParamValue =
  | string
  | number
  | null
  | undefined
  | (string | number | null)[];

/**
 * 파라미터 객체 타입 정의
 */
type ParamsObject = Record<string, QueryParamValue> & {
  query?: Record<string, QueryParamValue>;
};

/**
 * URL 쿼리 파라미터 직렬화 함수
 * @param paramObj 파라미터 객체
 * @returns 직렬화된 쿼리 문자열
 */
const paramsSerializer = (paramObj: ParamsObject): string => {
  const params = new URLSearchParams();

  // query 프로퍼티가 있으면 해당 객체를 사용, 없으면 paramObj 자체를 사용
  const paramsObject = paramObj.query ? paramObj.query : paramObj;

  Object.entries(paramsObject).forEach(([key, value]) => {
    // value가 undefined, null, 'null', 또는 빈 문자열이면 건너뜀
    if (
      value === undefined ||
      value === null ||
      value === 'null' ||
      value === ''
    ) {
      return;
    }

    // 배열인 경우 각 요소를 개별적으로 처리
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      value.forEach((item) => {
        if (
          item === undefined ||
          item === null ||
          item === 'null' ||
          item === ''
        )
          return;
        params.append(key, String(item));
      });
    } else {
      // 단일 값인 경우 문자열로 변환 후 추가
      params.append(key, String(value));
    }
  });

  return params.toString();
};

/**
 * API 요청을 위한 axios 클라이언트 인스턴스 생성
 */
export const axiosClient: AxiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  paramsSerializer: {
    serialize: paramsSerializer,
  },
});

/**
 * 인증이 필요없는 API 요청을 위한 axios 클라이언트 인스턴스 생성
 */
export const axiosNoAuthClient: AxiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  timeout: 60 * 1000,
  paramsSerializer: {
    serialize: paramsSerializer,
  },
});

/**
 * 요청 인터셉터 설정
 * - Authorization 헤더 추가 및 withCredentials 설정
 * - X-Space-Id 헤더 추가 (특정 URL만)
 */
axiosClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Authorization 헤더 추가
    attachAuthHeader(config);

    // X-Space-Id 헤더 추가 (특정 URL에만)
    attachSpaceIdHeader(config);

    // X-Device-Id 헤더 추가
    const deviceId = getDeviceId();
    if (deviceId) {
      config.headers['X-Device-Id'] = deviceId;
    }

    // X-Current-Url 헤더 추가
    if (typeof window !== 'undefined') {
      config.headers['X-Current-Url'] = window.location.href;
    }

    // X-Region 헤더 추가
    const region = getRegion();
    if (region) {
      config.headers['X-Region'] = region;
    }

    // X-Network-Info 헤더 추가
    const networkInfo = getNetworkInfo();
    if (networkInfo) {
      config.headers['X-Network-Info'] = networkInfo;
    }

    // traceparent / X-Trace-Id 헤더 추가 (호출부가 이미 세팅했으면 존중)
    if (!config.headers.has('traceparent')) {
      const trace = getFlowTraceContext();
      config.headers['traceparent'] = trace.traceparent;
      config.headers['X-Trace-Id'] = trace.traceId;
    }

    // FE 서버(SSR/route handler)발 호출만 stdout에 trace 로그 1줄 남긴다
    if (typeof window === 'undefined') {
      console.log(
        formatTraceLog(
          String(config.headers.get('traceparent')),
          config.method ?? '',
          `${config.baseURL ?? ''}${config.url ?? ''}`,
        ),
      );
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

/**
 * 인증 불필요 클라이언트 요청 인터셉터
 * - X-Device-Id 헤더 추가
 */
axiosNoAuthClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const deviceId = getDeviceId();
    if (deviceId) {
      config.headers['X-Device-Id'] = deviceId;
    }

    // X-Current-Url 헤더 추가
    if (typeof window !== 'undefined') {
      config.headers['X-Current-Url'] = window.location.href;
    }

    // X-Region 헤더 추가
    const region = getRegion();
    if (region) {
      config.headers['X-Region'] = region;
    }

    // X-Network-Info 헤더 추가
    const networkInfo = getNetworkInfo();
    if (networkInfo) {
      config.headers['X-Network-Info'] = networkInfo;
    }

    // traceparent / X-Trace-Id 헤더 추가 (호출부가 이미 세팅했으면 존중)
    if (!config.headers.has('traceparent')) {
      const trace = getFlowTraceContext();
      config.headers['traceparent'] = trace.traceparent;
      config.headers['X-Trace-Id'] = trace.traceId;
    }

    // FE 서버(SSR/route handler)발 호출만 stdout에 trace 로그 1줄 남긴다
    if (typeof window === 'undefined') {
      console.log(
        formatTraceLog(
          String(config.headers.get('traceparent')),
          config.method ?? '',
          `${config.baseURL ?? ''}${config.url ?? ''}`,
        ),
      );
    }

    return config;
  },
);

/**
 * 응답 인터셉터 설정
 * - 에러 응답 처리 및 토큰 갱신
 */
axiosClient.interceptors.response.use(handleResponse, (error) =>
  handleResponseError(error, axiosClient),
);
